import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Container,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from "@mui/material";
import { acceptShareRequest, acceptShareSuccess } from "../../../redux/projects/projectsSlice";
import { fastApiURL } from "../../../utils/config";
import default_theme from "../../../assets/themes/themes";

const AcceptProjectSharePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sharesLoading, sharesError } = useSelector((state) => state.projects);
  const authState = useSelector((state) => state.auth);
  const userId = authState?.user?.id;

  const token = searchParams.get("token");
  const [accepted, setAccepted] = useState(false);
  const [projectId, setProjectId] = useState(null);

  useEffect(() => {
    if (!token) {
      // No token in URL
      return;
    }
  }, [token]);

  // Watch for successful acceptance
  useEffect(() => {
    if (accepted && projectId) {
      // Redirect to project after 2 seconds
      const timer = setTimeout(() => {
        navigate(`/workspaces/projects/${projectId}`);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [accepted, projectId, navigate]);

  const handleAccept = async () => {
    if (!token) {
      return;
    }

    if (!userId) {
      // User not authenticated - redirect to login
      navigate("/login");
      return;
    }

    dispatch(acceptShareRequest({ token }));
    
    // Call API directly to get project_id for navigation
    try {
      const response = await fetch(`${fastApiURL}/api/projects/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-id": String(userId),
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `Failed to accept invitation: ${response.status}` }));
        throw new Error(errorData.detail || errorData.message || `Failed to accept invitation: ${response.status}`);
      }

      const result = await response.json();
      if (result && result.project_id) {
        setProjectId(result.project_id);
        setAccepted(true);
        dispatch(acceptShareSuccess());
      }
    } catch (error) {
      console.error("Error accepting invitation:", error);
    }
  };

  if (!token) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Invalid invitation link. No token provided.</Alert>
      </Container>
    );
  }

  if (accepted) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              Invitation accepted successfully! Redirecting to project...
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate(`/workspaces/projects/${projectId}`)}
              sx={{
                backgroundColor: default_theme.palette.other.chatGreen,
                "&:hover": {
                  backgroundColor: default_theme.palette.other.chatGreenHover,
                },
              }}
            >
              Go to Project
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Accept Project Invitation
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You have been invited to collaborate on a project. Click the button below to accept the invitation.
          </Typography>
          {sharesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sharesError}
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={sharesLoading}
              sx={{
                backgroundColor: default_theme.palette.other.chatGreen,
                "&:hover": {
                  backgroundColor: default_theme.palette.other.chatGreenHover,
                },
              }}
            >
              {sharesLoading ? <CircularProgress size={24} /> : "Accept Invitation"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => navigate("/workspaces")}
              disabled={sharesLoading}
            >
              Decline
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AcceptProjectSharePage;
